// A box on the System overview: NextAdmin demo `/saas` metric card (title, status badges, usage bars, sparklines).
import { Link } from 'react-router-dom';
import { Sparkline } from '../../../components/charts/index.js';
import { buttonStyles } from '../../../components/tailgrids/core/button.js';
import { Badge, Card } from '../../../components/ui/index.js';
import type { SystemHostSummary } from '../../../generated/api/types.gen.js';
import { formatAgo, formatBytes, formatRatio, formatUptime } from './metric-format.js';
import { UsageRow } from './UsageRow.js';

const ROLE_LABELS: Record<string, string> = {
  'control-plane': 'Control plane',
  engine: 'Engine',
  worker: 'Worker',
};

export function roleLabel(role: string | null): string {
  return role ? (ROLE_LABELS[role] ?? role) : 'Unknown role';
}

export function HostCard({ host, nowMs }: { host: SystemHostSummary; nowMs: number }) {
  const facts = [
    host.cpuCount !== null ? `${host.cpuCount} ${host.cpuCount === 1 ? 'CPU' : 'CPUs'}` : null,
    host.memoryTotalBytes !== null ? `${formatBytes(host.memoryTotalBytes)} RAM` : null,
    host.online ? `up ${formatUptime(host.uptimeSeconds)}` : `last seen ${formatAgo(host.lastSeenAt, nowMs)}`,
  ].filter((fact): fact is string => fact !== null);

  return (
    <Card
      title={host.id}
      className="xl:col-span-4"
      actions={
        <Link
          to={`/admin/system/hosts/${encodeURIComponent(host.id)}`}
          className={buttonStyles({ appearance: 'outline', size: 'xs', className: 'px-3 py-1.5' })}
          aria-label={`Open ${host.id}`}
        >
          Open
        </Link>
      }
    >
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={host.online ? 'success' : 'danger'} dot>
          {host.online ? 'Online' : 'Offline'}
        </Badge>
        <Badge variant="neutral">{roleLabel(host.role)}</Badge>
      </div>
      <p className="mt-2 text-xs text-muted">{facts.join(' · ')}</p>
      <div className="mt-4 space-y-3">
        <UsageRow label="CPU" ratio={host.cpuBusy} />
        <UsageRow label="Memory" ratio={host.memoryUsed} />
        <UsageRow label="Fullest disk" ratio={host.diskUsedMax} />
        {host.gpuBusy !== null && <UsageRow label="GPU" ratio={host.gpuBusy} />}
      </div>
      <div className="mt-4 grid grid-cols-2 gap-4">
        <div>
          <p className="mb-1 text-xs text-muted">CPU, last hour</p>
          <Sparkline values={host.cpuTrend} title={`${host.id} CPU busy over the last hour`} />
        </div>
        <div>
          <p className="mb-1 text-xs text-muted">Memory, last hour</p>
          <Sparkline values={host.memoryTrend} title={`${host.id} memory used over the last hour`} tone="teal" />
        </div>
      </div>
      {host.loadPerCpu !== null && (
        <p className="mt-3 text-xs text-muted">
          Load {formatRatio(host.loadPerCpu)} of its CPUs
          {host.temperatureMax !== null ? ` · hottest sensor ${Math.round(host.temperatureMax)} °C` : ''}
        </p>
      )}
    </Card>
  );
}
